import React, { useState, useEffect } from "react";
import { X, ChevronLeft, Check } from "lucide-react";
import { useFontSize } from "../../context/FontSizeContext";
import { ImContrast } from "react-icons/im";
import { CgZoomIn } from "react-icons/cg";
import { useTheme } from "../../context/ThemeContext";
const contrastOptions = [
  { label: "Black on white", theme: "light" },
  { label: "White on black", theme: "dark" },
  { label: "Yellow on black", theme: "high-contrast" },
];

const OptionsPopup = ({ onClose }) => {
  const [view, setView] = useState("main");
  const { selectedContrast, setSelectedContrast } = useTheme();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", selectedContrast);
  }, [selectedContrast]);

  const handleContrastChange = (option) => {
    setSelectedContrast(option.theme);
    document.documentElement.setAttribute("data-theme", option.theme);
  };

  // Get global text size values from Context
  const { setFontScale, selectedTextSize, setSelectedTextSize } = useFontSize();

  // Handle text size change
  const handleTextSizeChange = (option) => {
    setSelectedTextSize(option); 

    // Define scaling values based on selection
    const scaleValues = {
      "Regular": 1,       // Default 100%
      "Large": 1.25,      // 125%
      "Extra large": 1.5  // 150%
    };

    setFontScale(scaleValues[option]); // Update global font scale
  };

  // Main Options Screen
  const renderMainView = () => (
    <div className="w-full max-w-2xl ">
      <h2 className="text-center text-4xl font-bold mb-8">Options</h2>

      {/* Options List */}
      <div className="rounded-md options-list">
        <div
          className="border-b option border-gray-300 px-6 py-6 text-3xl flex justify-between items-center cursor-pointer"
          onClick={() => setView("contrast")}
        >
          <div>
            <ImContrast className="option-icon" />
            <span> Contrast</span>
          </div>
          <span>➜</span>
        </div>

        <div
          className="px-6 py-6 text-3xl option flex justify-between items-center cursor-pointer"
          onClick={() => setView("textSize")}
        >
          <div>
            <CgZoomIn className="option-icon-text-resizer" />
            <span>Text size</span>
          </div>
          <span>➜</span>
        </div>
      </div>
    </div>
  );

  // Contrast Selection Screen
  const renderContrastView = () => (
    <div className="w-full max-w-2xl">
      <div className="flex items-center justify-between mb-8">
        <button onClick={() => setView("main")} className="absolute top-6 left-6 flex items-center text-3xl">
          <ChevronLeft size={40} /> Options
        </button>
        <h2 className="text-center text-4xl font-bold flex-1">Contrast</h2>
      </div>

      <div className="rounded-md">
        {contrastOptions.map((option) => (
          <div
            key={option.theme}
            className="border-b border-gray-300 px-6 py-6 text-3xl flex justify-between items-center cursor-pointer"
            onClick={() => handleContrastChange(option)}
          >
            <span>{option.label}</span>
            {selectedContrast === option.theme && <Check size={40} />}
          </div>
        ))}
      </div>
    </div>
  );

  // Text Size Selection Screen
  const renderTextSizeView = () => (
    <div className="w-full max-w-2xl">
      <div className="flex items-center justify-between mb-8">
        <button onClick={() => setView("main")} className="absolute top-6 left-6 flex items-center text-3xl">
          <ChevronLeft size={40} /> Options
        </button>
        <h2 className="text-center text-4xl font-bold flex-1">Text size</h2>
      </div>

      <div className="rounded-md">
        {["Regular", "Large", "Extra large"].map((option) => (
          <div
            key={option}
            className="border-b border-gray-300 px-6 py-6 text-3xl flex justify-between items-center cursor-pointer"
            onClick={() => handleTextSizeChange(option)}
          >
            <span>{option}</span>
            {selectedTextSize === option && <Check size={40} />}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 theme-background flex z-[2] flex-col items-center justify-center p-12">
      {view === "main" && renderMainView()}
      {view === "contrast" && renderContrastView()}
      {view === "textSize" && renderTextSizeView()}

      {/* Close Button */}
      <button className="absolute top-6 right-6" onClick={onClose}>
        <X size={50} />
      </button>
    </div>
  );
};

export default OptionsPopup;
